import CatExposuresGrid from "./CatExposuresGrid";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import Metrics from "components/common/Metrics";
import { usePricing } from "components/customPricing/dnfModel1/pricingHooks";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as catExposuresStore from "store/catExposures";
import { useSovAnalytics } from "store/hooks/sovHooks";
import * as stateSelectors from "store/selectors/stateSelectors";

const EMPTY_OBJ = Object.freeze({});

const extractPerilMetrics = (exposures) => {
  const perilMap = exposures.reduce((acc, exposure) => {
    const peril = exposure._SUBLIMIT_ZONE?.split?.(".")?.[0] ?? "Unclassified";

    acc[peril] =
      acc[peril] == null
        ? { peril, totalValue: exposure._VALUE ?? 0 }
        : {
            ...acc[peril],
            totalValue: acc[peril].totalValue + (exposure._VALUE ?? 0),
          };

    return acc;
  }, {});

  const metrics = Object.values(perilMap).map(({ peril, totalValue }) => ({
    label: peril,
    value: totalValue,
    prefix: "$",
  }));

  return metrics.length === 0 ? [{ label: "No Data", value: "-" }] : metrics;
};

const CatExposuresSummary = ({ catModel }) => {
  const theme = useTheme();
  const programPricing = useSovAnalytics();

  const pricing = useMemo(() => {
    const values = programPricing?.values;
    const pricing = values?.catModels?.length
      ? values?.sovCat?.[catModel]
      : values?.sovCat;
    return pricing ?? EMPTY_OBJ;
  }, [programPricing, catModel]);

  const { data: analyticsConfig } = staticDataService.useConfigQuery(
    "analytics"
  );

  const priorState = useSelector(stateSelectors.selectPriorState);
  const priorProgramPricing = priorState?.analytics?.programPricing;
  const priorPricing = useMemo(() => {
    const engineConfig =
      analyticsConfig?.engineConfig?.[priorProgramPricing?.engine] ?? {};
    if (engineConfig?.useMultiCatModelMode) {
      return priorProgramPricing?.values?.sovCat?.[catModel] ?? EMPTY_OBJ;
    } else if (catModel === engineConfig?.catModels?.[0]) {
      return priorProgramPricing?.values?.sovCat ?? EMPTY_OBJ;
    } else {
      return [];
    }
  }, [analyticsConfig, priorProgramPricing, catModel]);

  const dispatch = useDispatch();

  const catExposures = useSelector(catExposuresStore.select);

  const updateExposure = (id) => ({ field, value }) => {
    if (!pricing?.[id]) {
      return;
    }

    setTimeout(
      () =>
        dispatch(
          catExposuresStore.update({
            sovCatOverrides: {
              ...catExposures?.sovCatOverrides,
              [id]: {
                ...catExposures?.sovCatOverrides?.[id],
                [field]: value ?? undefined,
              },
            },
          })
        ),
      0
    );
  };

  const { isFetching } = usePricing();

  const allRegionPerils = useMemo(() => {
    const currentKeys = Object.keys(pricing);
    const priorKeys = Object.keys(priorPricing);
    return Array.from(new Set([...currentKeys, ...priorKeys]));
  }, [pricing, priorPricing]);

  const exposures = allRegionPerils.map((regionPerilKey) => {
    const current = pricing?.[regionPerilKey] ?? null;
    const prior = priorPricing?.[regionPerilKey] ?? null;
    const rp = current?._REGION_PERIL ?? regionPerilKey;

    const overrides = Object.fromEntries(
      Object.entries(catExposures?.sovCatOverrides?.[rp] ?? {}).filter(
        ([_, value]) => value != null
      )
    );

    return {
      id: rp,
      _REGION_PERIL: rp,
      ...(current ?? {}),
      ...overrides,
      _EXPIRING_VALUE: prior?._VALUE ?? undefined,
    };
  });

  const metrics = extractPerilMetrics(exposures);

  return (
    <Component title={"SOV"} isFetching={isFetching}>
      <Box
        sx={{
          margin: "0.5rem 0",
          padding: "0.875rem 0.375rem",
          background: theme.brand.gray[500],
          borderRadius: "0.625rem",
        }}
      >
        <Metrics metrics={metrics} />
      </Box>
      <Disabled ifReadOnly>
        <CatExposuresGrid
          catModel={catModel}
          exposures={[...exposures]}
          updateExposure={updateExposure}
          columnEditabilityOverride={{
            _REGION_PERIL: false,
          }}
          loading={isFetching}
          sortable={true}
          initialSortModel={[{ field: "_REGION_PERIL", sort: "asc" }]}
          hideExpiring={Object.keys(priorPricing).length === 0}
        />
      </Disabled>
    </Component>
  );
};

export default CatExposuresSummary;
